// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnInit,
} from '@angular/core';

// Material
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

// Local library
import { ticketRequest } from '@amwager/util-ts';
import { MatNotificationService,
TranslatePipe
} from '@amwager/util';

// Third party
import * as _ from 'lodash';

/**
 * Material port of the mobile `AbcComponent`. Lists the ABC ticket
 * groups (All As, All As and Bs, etc.) and lets the user submit /
 * save individual rows or submit / save / close all rows at once.
 *
 * - Individual `submitBet` / `saveBet` rows call back into
 *   `BetService.betNow` directly via the `betNow` callback that
 *   `BetPreviewComponent` passes in via the dialog payload.
 * - `submitAll` / `saveAll` close the dialog with the ticket so the
 *   caller (`BetPreviewComponent`) can hand the consolidated request
 *   to `BetService.betNow`.
 */
@Component({
  selector: 'feature-mat-abc',
  templateUrl: './abc.component.html',
  styleUrls: ['./abc.component.scss'],
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule,
    TranslatePipe
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcComponent implements OnInit {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  betNow!: (event: any) => void;

  eventName!: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  abcTicket: any[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedRace: any;
  balanceAmount: number = 0;

  /** Mirrors the labels shown by the mobile app for each ABC group. */
  readonly groupLabels = [
    'All As',
    'All As and Bs',
    'As with One B',
    'As and Two Bs',
    'As with One C',
  ];

  constructor(
    public dialogRef: MatDialogRef<AbcComponent>,
    private readonly elementRef: ElementRef,
    private readonly cdRef: ChangeDetectorRef,
    private readonly matNotificationService: MatNotificationService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      payload: any;
      actionShowYesBtn?: boolean;
      actionShowNoBtn?: boolean;
    }
  ) {}

  ngOnInit(): void {
    const p = this.data?.payload ?? {};
    this.eventName = p.eventName;
    this.abcTicket = p.abcTicket ?? [];
    this.selectedRace = p.selectedRace;
    this.balanceAmount = p.balanceAmount ?? 0;
    this.betNow = p.betNow;
  }

  /**
   * Build a fresh `ticketRequest` containing all wagers across every
   * group that hasn't been submitted, saved, or removed. The flag
   * `isIndividual` controls the DutchType (0 for individual, 3 for
   * batch) so the API treats it the same as the mobile flow.
   *
   * @param {boolean} isIndividual - Use 0 for an individual ticket; 3 for a batch
   * @returns {ticketRequest}
   */
  createNewABCTicket(isIndividual: boolean): ticketRequest {
    const wagersList = [];
    for (let i = 0; i < this.abcTicket.length; i++) {
      if (
        (this.abcTicket[i].submitted === undefined ||
          this.abcTicket[i].submitted === false) &&
        (this.abcTicket[i].saved === undefined ||
          this.abcTicket[i].saved === false) &&
        (this.abcTicket[i].removed === undefined ||
          this.abcTicket[i].removed === false)
      ) {
        for (
          let j = 0;
          j < this.abcTicket[i].ABCTicket.Wagers.length;
          j++
        ) {
          wagersList.push(this.abcTicket[i].ABCTicket.Wagers[j]);
        }
      }
    }

    return new ticketRequest(
      this.abcTicket[0].ABCTicket.RunId,
      this.abcTicket[0].ABCTicket.EventId,
      this.abcTicket[0].ABCTicket.RaceId,
      wagersList,
      isIndividual ? 0 : 3,
      undefined
    );
  }

  /**
   * Submit a single ABC row via the parent's `betNow` callback.
   * @param {number} index - Group index
   * @returns {void}
   */
  submitBet(index: number): void {
    if (!this.abcTicket?.[index]) return;

    if (
      this.abcTicket[index].ABCTicket.calcAmount() > this.balanceAmount
    ) {
      this.matNotificationService.showAlert(
        'Info',
        'Insufficient Funds.',
        '',
        '',
        'Deposit Now'
      );
      return;
    }

    if (
      this.abcTicket[index].ABCTicket.Error === undefined ||
      this.abcTicket[index].ABCTicket.Error === null
    ) {
      this.abcTicket[index].submitted = true;
      this.abcTicket[index].ABCTicket.DutchType = 0;
      this.betNow?.({
        betType: 'bet-now',
        typeIs: 'abc',
        abcTicket: this.abcTicket[index].ABCTicket,
      });
      this.cdRef.markForCheck();
    } else {
      const errMsg = this.elementRef.nativeElement.querySelectorAll(
        '.current-bets #error-index-' + index
      );

      _.forEach(errMsg, (html: HTMLElement) => {
        if (html) html.innerHTML = this.abcTicket[index].ABCTicket.Error;
      });
    }
  }

  /**
   * Save a single ABC row via the parent's `betNow` callback.
   * @param {number} index - Group index
   * @returns {void}
   */
  saveBet(index: number): void {
    if (!this.abcTicket?.[index]) return;

    this.abcTicket[index].saved = true;
    this.abcTicket[index].ABCTicket.DutchType = 0;
    this.betNow?.({
      betType: 'save-bet',
      typeIs: 'abc',
      abcTicket: this.abcTicket[index].ABCTicket,
    });
    this.cdRef.markForCheck();
  }

  /**
   * Mark a single ABC row as removed (won't be included in submit /
   * save all and won't show its action buttons anymore).
   * @param {number} index - Group index
   * @returns {void}
   */
  removeBet(index: number): void {
    if (!this.abcTicket?.[index]) return;
    this.abcTicket[index].removed = true;
    this.cdRef.markForCheck();
  }

  /**
   * Submit every still-active row as a single batch ticket.
   * @returns {void}
   */
  submitAll(): void {
    const abcTicket = this.createNewABCTicket(false);

    if (abcTicket.calcAmount() > this.balanceAmount) {
      this.matNotificationService.showAlert(
        'Info',
        'Insufficient Funds.',
        '',
        '',
        'Deposit Now'
      );
      return;
    }

    this.dialogRef.close({
      data: { betType: 'bet-now', typeIs: 'abc', abcTicket },
    });
  }

  /**
   * Save every still-active row as a single batch ticket.
   * @returns {void}
   */
  saveAll(): void {
    const abcTicket = this.createNewABCTicket(false);

    this.dialogRef.close({
      data: { betType: 'save-bet', typeIs: 'abc', abcTicket },
    });
  }

  /**
   * Cancel the dialog without dispatching any bet.
   * @returns {void}
   */
  close(): void {
    this.dialogRef.close({ data: null });
  }
}
